// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Admin settings panel for the legislative review application.
 *
 * Rendered inside the generic "App Settings" admin tab via the
 * application registration's AdminSettingsComponent.
 */
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui'
import { Input } from '@/components/ui'
import { Label } from '@/components/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui'
import { Plus, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  useLegislativeReviewAdminApi,
  type DatasourceConfig,
  type LegislativeReviewSettings,
} from '../services/admin.service'

export function LegislativeReviewAdminSettings() {
  const adminApi = useLegislativeReviewAdminApi()
  const queryClient = useQueryClient()

  const [datasources, setDatasources] = useState<DatasourceConfig[]>([])
  const [newSession, setNewSession] = useState<Record<string, string>>({})

  const { data: settings, isLoading } = useQuery({
    queryKey: ['legislative-review-settings'],
    queryFn: () => adminApi.fetchSettings(),
  })

  useEffect(() => {
    if (settings) {
      setDatasources(settings.datasources || [])
    }
  }, [settings])

  const saveMutation = useMutation({
    mutationFn: (data: LegislativeReviewSettings) => adminApi.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legislative-review-settings'] })
      toast.success('Settings saved')
    },
    onError: (error) => {
      toast.error('Failed to save settings', {
        description: error instanceof Error ? error.message : undefined,
      })
    },
  })

  const handleAddDatasource = () => {
    setDatasources([...datasources, { id: '', sessions: [] }])
  }

  const handleRemoveDatasource = (index: number) => {
    setDatasources(datasources.filter((_, i) => i !== index))
  }

  const handleDatasourceChange = (index: number, fields: Partial<DatasourceConfig>) => {
    const updated = [...datasources]
    updated[index] = { ...updated[index], ...fields }
    setDatasources(updated)
  }

  const handleAddSession = (dsIndex: number) => {
    const sessionValue = newSession[dsIndex]?.trim()
    if (!sessionValue) return

    const updated = [...datasources]
    if (!updated[dsIndex].sessions.includes(sessionValue)) {
      updated[dsIndex] = {
        ...updated[dsIndex],
        sessions: [...updated[dsIndex].sessions, sessionValue],
      }
      setDatasources(updated)
    }
    setNewSession({ ...newSession, [dsIndex]: '' })
  }

  const handleRemoveSession = (dsIndex: number, sessionIndex: number) => {
    const updated = [...datasources]
    updated[dsIndex] = {
      ...updated[dsIndex],
      sessions: updated[dsIndex].sessions.filter((_, i) => i !== sessionIndex),
    }
    setDatasources(updated)
  }

  const handleSave = () => {
    const validDatasources = datasources.filter((ds) => ds.id.trim())
    saveMutation.mutate({ datasources: validDatasources })
  }

  const handleKeyDown = (e: React.KeyboardEvent, dsIndex: number) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddSession(dsIndex)
    }
  }

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">Loading settings...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">Datasources</Label>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleAddDatasource}>
            <Plus className="h-4 w-4 mr-1" />
            Add Datasource
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {datasources.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border rounded-md">
          No datasources configured. Click "Add Datasource" to get started.
        </div>
      ) : (
        <div className="space-y-4">
          {datasources.map((ds, dsIndex) => (
            <Card key={dsIndex}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">
                    {ds.id || <span className="text-muted-foreground italic">New datasource</span>}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveDatasource(dsIndex)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor={`ds-id-${dsIndex}`} className="text-sm">
                    Datasource ID
                  </Label>
                  <Input
                    id={`ds-id-${dsIndex}`}
                    value={ds.id}
                    onChange={(e) => handleDatasourceChange(dsIndex, { id: e.target.value })}
                    placeholder="e.g., Maryland, Federal"
                    className="mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor={`ds-admin-org-${dsIndex}`} className="text-sm">
                      Admin Org ID
                    </Label>
                    <Input
                      id={`ds-admin-org-${dsIndex}`}
                      value={ds.adminOrgId || ''}
                      onChange={(e) =>
                        handleDatasourceChange(dsIndex, { adminOrgId: e.target.value || null })
                      }
                      placeholder="e.g., umd-org"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`ds-admin-ws-${dsIndex}`} className="text-sm">
                      Admin Workspace ID
                    </Label>
                    <Input
                      id={`ds-admin-ws-${dsIndex}`}
                      value={ds.adminWorkspaceId || ''}
                      onChange={(e) =>
                        handleDatasourceChange(dsIndex, { adminWorkspaceId: e.target.value || null })
                      }
                      placeholder="e.g., maryland-admin"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm">Sessions</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {ds.sessions.map((session, sessionIndex) => (
                      <span
                        key={sessionIndex}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-sm"
                      >
                        {session}
                        <button
                          type="button"
                          onClick={() => handleRemoveSession(dsIndex, sessionIndex)}
                          className="hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newSession[dsIndex] || ''}
                      onChange={(e) => setNewSession({ ...newSession, [dsIndex]: e.target.value })}
                      onKeyDown={(e) => handleKeyDown(e, dsIndex)}
                      placeholder="e.g., 2024RS, 2025RS"
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddSession(dsIndex)}
                      disabled={!newSession[dsIndex]?.trim()}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
